// src/util/network/RequisitionPool.ts
//
// The buyer's side of pooling (EE-253 P2b): submitted requisition lines wait
// in "Awaiting Inquiry", and the buyer builds Draft Inquiries from lines of
// any number of requisitions. Endpoints mirror
// backend/apis/v1/ecommerce/{Requisitions,Inquiries}.py.
//
// The pool rows are snake_case like the rest of the requisition API; the
// compose endpoints answer with an inquiry, camelCase like the inquiry API.
import { InquiryDetailType } from "types/InquiryTypes";
import { FetchError } from "exceptions/exceptions";
import { API_URL } from "./common";

const ECOMMERCE = `${API_URL}/v1/ecommerce`;

const headers = (token: string) => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${token}`,
});

const json = async (res: Response) => {
  if (res.ok) return res.json();
  throw new FetchError(await res.text(), res.status);
};

/** One line of a submitted requisition that no live Inquiry carries. */
export type AwaitingLineType = {
  key: string;
  requisition_item_id: number;
  requisition_id: number;
  requisition_no: string;
  line_no: number | null;
  type_code: string | null;
  type_label: string | null;
  // The Inquiry type the line goes on; lines of different types never share
  // an Inquiry.
  inquiry_type: string | null;
  sales_order_id: number | null;
  sales_order_no: string | null;
  quote_id: number | null;
  part_name: string | null;
  // The part's 3D model - its preview render is the row thumbnail.
  model_id: number | null;
  what: string;
  spec: string;
  pcs: number | null;
  required: string;
  required_amount: number | null;
  required_unit: string | null;
  required_by_date: string | null;
  supplier_id: number | null;
  supplier_name: string | null;
  requester_name: string | null;
  can_reject: boolean;
  // Batching (spec 6.6): raw material groups by the stock it is cut from,
  // every other type by the partner the requester suggested.
  stock_key: string | null;
  group_key: string;
  group_label: string;
  // Open Inquiries (Draft, or Approved and not sent) this line would join:
  // same stock, or same suggested partner.
  matching_drafts: MatchingDraftType[];
};

export type MatchingDraftType = {
  id: number;
  title: string | null;
  state: string | null;
  partner_name: string | null;
};

/** An Inquiry line as composing the chosen requisition lines would build it. */
export type ComposedItemType = {
  id: number | null;
  key: string;
  comment: string;
  // EE-253 P3: a raw-material line is one per stock - unit "lengths",
  // stockMm the length, quantity the count (the buyer's, if they set them on
  // the Draft; else 1 × everything needed).
  name?: string | null;
  unit: string | null;
  stockMm: number | null;
  quantity: number | null;
  quantityOverridden?: boolean;
  // The buyer's count and length, when they no longer hold every piece.
  shortMm?: number | null;
  requiredMm: number | null;
  required: string;
  sources: string[];
};

export type ComposePreviewType = {
  // Set when the server would refuse these lines (mixed types, a line already
  // on another Inquiry ...); nothing is written by a preview.
  error?: string;
  type?: string;
  inquiryType?: string;
  items: ComposedItemType[];
};

export type ComposeBodyType = {
  // Requisition line ids (REQUISITION_ITEMS.id).
  lines: number[];
  partnerId?: number | null;
  deadline?: string | null;
};

export const apiGetAwaitingEnquiry = (
  token: string
): Promise<AwaitingLineType[]> =>
  fetch(`${ECOMMERCE}/requisitions/awaiting-enquiry`, {
    headers: headers(token),
  }).then(json);

export const apiPreviewEnquiryLines = (
  token: string,
  body: ComposeBodyType & { inquiryId?: number | null }
): Promise<ComposePreviewType> =>
  fetch(`${ECOMMERCE}/inquiries/compose-preview`, {
    method: "POST",
    headers: headers(token),
    body: JSON.stringify(body),
  }).then(json);

export const apiCreateEnquiryFromLines = (
  token: string,
  body: ComposeBodyType
): Promise<InquiryDetailType> =>
  fetch(`${ECOMMERCE}/inquiries/from-requisition-lines`, {
    method: "POST",
    headers: headers(token),
    body: JSON.stringify(body),
  }).then(json);

/** Make a Draft Inquiry carry exactly these lines; the rest go back to the pool. */
export const apiSetEnquiryRequisitionLines = (
  token: string,
  inquiryId: number,
  lines: number[]
): Promise<InquiryDetailType> =>
  fetch(`${ECOMMERCE}/inquiries/${inquiryId}/requisition-lines`, {
    method: "PUT",
    headers: headers(token),
    body: JSON.stringify({ lines }),
  }).then(json);

/** Take one requisition line off a Draft Inquiry, back to the pool. */
export const apiReleaseEnquiryRequisitionLine = (
  token: string,
  inquiryId: number,
  requisitionItemId: number
): Promise<InquiryDetailType> =>
  fetch(
    `${ECOMMERCE}/inquiries/${inquiryId}/requisition-lines/${requisitionItemId}`,
    {
      method: "DELETE",
      headers: headers(token),
    }
  ).then(json);

/** What one Inquiry line buys (EE-253 P3). Only the fields sent change. On a
 * raw-material line bought in stock lengths, quantity is the number of
 * lengths and null puts the suggestion back. Draft / Approved only. */
export type LinePurchaseBodyType = {
  quantity?: number | null;
  stockLengthMm?: number;
  serviceQuantity?: number | null;
  serviceUnit?: string | null;
};

export const apiSetEnquiryLinePurchase = (
  token: string,
  inquiryId: number,
  itemId: number,
  body: LinePurchaseBodyType
): Promise<InquiryDetailType> =>
  fetch(`${ECOMMERCE}/inquiries/${inquiryId}/items/${itemId}/purchase`, {
    method: "PUT",
    headers: headers(token),
    body: JSON.stringify(body),
  }).then(json);

// Mirrors modules/purchasing/application/order_panel.order_purchasing.
export type OrderPurchasingLineType = {
  requisitionItemId: number;
  lineNo: number | null;
  type: string | null;
  typeLabel: string | null;
  // Raw material: the stock (grade, cross-section); `part` is what it is cut for.
  what: string;
  part: string | null;
  required: string;
  requiredBy: string | null;
  inquiryId: number | null;
  inquiryState: string | null;
  supplier: string | null;
  lineState: string | null;
  lineStateLabel: string | null;
  // "mm" on raw material bought in stock lengths, else pieces.
  readyGot: number | null;
  readyNeed: number | null;
  readyUnit: string | null;
  // "1 605 / 1 605 mm", formatted like `required`; null when nothing to count.
  ready: string | null;
  // Øre; null until the Inquiry has its prices.
  costShareCents: number | null;
  transportShareCents: number | null;
};

export type OrderPurchasingType = {
  orderId: number;
  requisitions: {
    id: number;
    requisitionNo: string;
    status: string | null;
    requiredBy: string | null;
    lines: OrderPurchasingLineType[];
  }[];
  // complete: every line on an Inquiry has both of its shares.
  totals: { goodsCents: number; transportCents: number; complete: boolean };
};

export const apiGetOrderPurchasing = (
  token: string,
  orderId: number
): Promise<OrderPurchasingType> =>
  fetch(`${ECOMMERCE}/orders/${orderId}/purchasing`, {
    headers: headers(token),
  }).then(json);
